import { useMutation, useQueryClient } from "@tanstack/react-query";

import { scheduledAgentMessagesQueryKey } from "@/hooks/queries/useGetScheduledAgentMessages";
import { popupNotificationsQueryKey } from "@/lib/constants";
import { guacHttpClient } from "@/lib/http";
import { HttpResponse, NotificationResponse } from "@/types";

// Takes over a call the candidate asked for, which permanently disables the
// scheduling agent on that candidacy. 409s when the two-minute window has
// already closed or someone else took the conversation first.
export function useAcceptCallRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      candidacyId,
    }: {
      candidacyId: string;
      notificationId: number;
    }) => {
      const path = "staffer-agent-conversations/call-request/accept";
      await guacHttpClient.post<
        unknown,
        HttpResponse<unknown>,
        { candidacyId: string }
      >(path, { candidacyId });
    },
    onSuccess: (_data, { candidacyId, notificationId }) => {
      // The agent is off for good, so its queued follow-ups will never fire.
      void queryClient.invalidateQueries({
        queryKey: scheduledAgentMessagesQueryKey(candidacyId),
      });

      // The banner has served its purpose — drop it rather than letting it
      // count down over a conversation the staffer now owns.
      queryClient.setQueryData(
        popupNotificationsQueryKey,
        (cached?: NotificationResponse[]): NotificationResponse[] | undefined =>
          cached?.filter((notification) => notification.id !== notificationId),
      );
    },
  });
}
